import React, { useState, useCallback } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  websiteTypes,
  pageCountOptions,
  cmsOptions,
  websiteFeatures,
  platformOptions,
  loginMethods,
  paymentTypes,
  marketingGoals,
  marketingServices,
  marketingBudgetRanges,
  socialPlatforms,
  logoTypes,
  brandStyles,
  videoTypes,
  videoDurations,
  accentOptions,
  voiceOptions,
  elearningTypes,
  linkedinProfileTypes,
  linkedinServices,
  postingFrequency,
  contentTypes,
  postLaunchServices,
  services
} from '../data/mock';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Move these components OUTSIDE the main component to prevent re-creation on each render
const FormSection = ({ title, children, color = 'emerald' }) => (
  <section className={cn(
    'p-4 rounded-xl border',
    color === 'emerald' ? 'bg-slate-50 border-slate-100' : 'bg-amber-50 border-amber-200'
  )}>
    <h3 className={cn(
      'text-xs font-semibold uppercase tracking-wider mb-4 flex items-center gap-2',
      color === 'emerald' ? 'text-emerald-700' : 'text-amber-700'
    )}>
      <span className={cn(
        'w-2 h-2 rounded-full',
        color === 'emerald' ? 'bg-emerald-500' : 'bg-amber-500'
      )}></span>
      {title}
    </h3>
    {children}
  </section>
);

const FieldRow = ({ children, cols = 2 }) => (
  <div className={cn('grid gap-4', cols === 2 ? 'grid-cols-2' : 'grid-cols-1')}>
    {children}
  </div>
);

const FieldGroup = ({ label, children, className = '' }) => (
  <div className={cn('space-y-2', className)}>
    <Label className="text-sm">{label}</Label>
    {children}
  </div>
);

const CheckboxGroup = ({ options, selectedValues, onChange, cols = 2 }) => (
  <div className={cn('grid gap-2', cols === 2 ? 'grid-cols-2' : cols === 3 ? 'grid-cols-3' : cols === 4 ? 'grid-cols-4' : 'grid-cols-1')}>
    {options.map(option => (
      <label
        key={option.value}
        className="flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors text-sm"
      >
        <Checkbox
          checked={selectedValues.includes(option.value)}
          onCheckedChange={() => onChange(option.value)}
        />
        <span className="text-slate-700 text-xs">{option.label}</span>
      </label>
    ))}
  </div>
);

const initialFormState = {
  // Section 1: Basic Information
  fullName: '',
  companyName: '',
  country: '',
  existingWebsite: '',
  
  // Website fields
  hasWebsite: '',
  existingWebsiteUrl: '',
  websiteType: '',
  websiteTypeOther: '',
  pageCount: '',
  contentReady: '',
  wantContentCreation: '',
  cmsPreference: '',
  cmsOther: '',
  websiteFeaturesNeeded: [],
  websiteFeaturesOther: '',
  productCount: '',
  hasBrandedEmail: '',
  
  // Mobile App fields
  appType: '',
  appPlatform: '',
  hasUiDesign: '',
  wantUiDesign: '',
  appCoreFeatures: '',
  needsAuth: '',
  authMethods: [],
  needsPayment: '',
  paymentType: '',
  
  // Digital Marketing fields
  marketingGoal: '',
  targetMarket: '',
  marketingServicesNeeded: [],
  marketingBudget: '',
  seoWebsiteUrl: '',
  seoCurrentlyActive: '',
  hasAdAccount: '',
  socialPlatforms: [],
  needsSocialContent: '',
  
  // Logo & Branding fields
  brandType: '',
  existingLogo: null,
  logoType: '',
  brandStyle: '',
  brandStyleOther: '',
  logoReferences: '',
  
  // Animation fields
  videoType: '',
  videoTypeOther: '',
  videoDuration: '',
  hasScript: '',
  wantScriptWriting: '',
  needsVoiceOver: '',
  accentPreference: '',
  voicePreference: '',
  
  // eLearning fields
  courseType: '',
  moduleCount: '',
  courseContentReady: '',
  wantContentStructure: '',
  needsLms: '',
  existingLms: '',
  preferredLms: '',
  
  // LinkedIn fields
  linkedinProfileType: '',
  linkedinServicesNeeded: [],
  postingFrequency: '',
  targetAudience: '',
  hasOutreachMessages: '',
  wantOutreachCreation: '',
  
  // Content Writing fields
  contentTypesNeeded: [],
  contentDetails: '',
  
  // Project Clarity
  decisionMaker: '',
  decisionMakerDetails: '',
  successAndNonNegotiables: '',
  postLaunchNeeds: [],
  additionalNotes: ''
};

export const DynamicForm = ({ selectedServices, selectedPackage, onSubmitSuccess }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleMultiSelect = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleChange('existingLogo', file);
    }
  }, [handleChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        selectedServices,
        selectedPackage,
        existingLogo: formData.existingLogo ? formData.existingLogo.name : null,
        submittedAt: new Date().toISOString()
      };
      
      const response = await fetch(`${BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });
      
      if (!response.ok) {
        throw new Error('Submission failed');
      }
      
      setIsSubmitted(true);
      if (onSubmitSuccess) {
        onSubmitSuccess(formData);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedServiceNames = () => {
    return selectedServices
      .map(id => services.find(s => s.id === id)?.name)
      .filter(Boolean);
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData(initialFormState);
  };

  if (isSubmitted) {
    return (
      <div className="h-full flex items-center justify-center px-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">
            Thank You!
          </h2>
          <p className="text-slate-500 leading-relaxed mb-6">
            We've received your project details. Our team will review everything and 
            get back to you within <strong className="text-slate-700">24-48 hours</strong> with 
            next steps and a detailed proposal.
          </p>
          <Button
            onClick={resetForm}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800 mb-1">
            Tell Us About Your Project
          </h2>
          <p className="text-sm text-slate-500">
            Selected: {getSelectedServiceNames().join(', ') || 'Custom Project'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Basic Information */}
          <FormSection title="Basic Information">
            <div className="space-y-4">
              <FieldRow>
                <FieldGroup label="Full Name *">
                  <Input
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    placeholder="Your full name"
                    required
                    className="bg-white"
                  />
                </FieldGroup>
                <FieldGroup label="Company / Brand Name">
                  <Input
                    value={formData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    placeholder="Your company name"
                    className="bg-white"
                  />
                </FieldGroup>
              </FieldRow>
              <FieldRow>
                <FieldGroup label="Country / Target Market">
                  <Input
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    placeholder="e.g., USA, Global"
                    className="bg-white"
                  />
                </FieldGroup>
                <FieldGroup label="Website (if any)">
                  <Input
                    value={formData.existingWebsite}
                    onChange={(e) => handleChange('existingWebsite', e.target.value)}
                    placeholder="https://yourwebsite.com"
                    className="bg-white"
                  />
                </FieldGroup>
              </FieldRow>
            </div>
          </FormSection>

          {/* Website Development Section */}
          {selectedServices.includes('website') && (
            <FormSection title="Website Development">
              <div className="space-y-4">
                <FieldRow>
                  <FieldGroup label="Do you already have a website?">
                    <Select value={formData.hasWebsite} onValueChange={(v) => handleChange('hasWebsite', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  {formData.hasWebsite === 'yes' && (
                    <FieldGroup label="Existing Website URL">
                      <Input
                        value={formData.existingWebsiteUrl}
                        onChange={(e) => handleChange('existingWebsiteUrl', e.target.value)}
                        placeholder="https://..."
                        className="bg-white"
                      />
                    </FieldGroup>
                  )}
                </FieldRow>

                <FieldRow>
                  <FieldGroup label="Type of website required">
                    <Select value={formData.websiteType} onValueChange={(v) => handleChange('websiteType', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {websiteTypes.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  <FieldGroup label="Approximate number of pages">
                    <Select value={formData.pageCount} onValueChange={(v) => handleChange('pageCount', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {pageCountOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </FieldRow>

                {formData.websiteType === 'other' && (
                  <FieldGroup label="Describe the website type">
                    <Input
                      value={formData.websiteTypeOther}
                      onChange={(e) => handleChange('websiteTypeOther', e.target.value)}
                      placeholder="Describe your website type"
                      className="bg-white"
                    />
                  </FieldGroup>
                )}

                {formData.websiteType === 'ecommerce' && (
                  <FieldGroup label="How many products do you want to add?">
                    <Input
                      value={formData.productCount}
                      onChange={(e) => handleChange('productCount', e.target.value)}
                      placeholder="e.g., 50, 100, 500+"
                      className="bg-white"
                    />
                  </FieldGroup>
                )}

                <FieldRow>
                  <FieldGroup label="Is your website content ready?">
                    <Select value={formData.contentReady} onValueChange={(v) => handleChange('contentReady', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="partially">Partially</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  {(formData.contentReady === 'partially' || formData.contentReady === 'no') && (
                    <FieldGroup label="Do you want us to create content?">
                      <Select value={formData.wantContentCreation} onValueChange={(v) => handleChange('wantContentCreation', v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  )}
                </FieldRow>

                <FieldRow>
                  <FieldGroup label="CMS Preference">
                    <Select value={formData.cmsPreference} onValueChange={(v) => handleChange('cmsPreference', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select CMS" /></SelectTrigger>
                      <SelectContent>
                        {cmsOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  {formData.cmsPreference === 'other' && (
                    <FieldGroup label="Specify CMS">
                      <Input
                        value={formData.cmsOther}
                        onChange={(e) => handleChange('cmsOther', e.target.value)}
                        placeholder="Your preferred CMS"
                        className="bg-white"
                      />
                    </FieldGroup>
                  )}
                </FieldRow>

                <FieldGroup label="Features required (select all that apply)">
                  <CheckboxGroup
                    options={websiteFeatures}
                    selectedValues={formData.websiteFeaturesNeeded}
                    onChange={(v) => handleMultiSelect('websiteFeaturesNeeded', v)}
                  />
                </FieldGroup>

                {formData.websiteFeaturesNeeded.includes('other') && (
                  <FieldGroup label="Describe required features">
                    <Textarea
                      value={formData.websiteFeaturesOther}
                      onChange={(e) => handleChange('websiteFeaturesOther', e.target.value)}
                      placeholder="Describe additional features"
                      className="bg-white min-h-[60px]"
                    />
                  </FieldGroup>
                )}

                <FieldGroup label="Do you have branded emails working with your domain?">
                  <Select value={formData.hasBrandedEmail} onValueChange={(v) => handleChange('hasBrandedEmail', v)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="not-sure">Not sure</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldGroup>
              </div>
            </FormSection>
          )}

          {/* Mobile App Section */}
          {selectedServices.includes('mobile-app') && (
            <FormSection title="Mobile App Development">
              <div className="space-y-4">
                <FieldRow>
                  <FieldGroup label="Type of app">
                    <Input
                      value={formData.appType}
                      onChange={(e) => handleChange('appType', e.target.value)}
                      placeholder="Describe your app idea"
                      className="bg-white"
                    />
                  </FieldGroup>
                  <FieldGroup label="Platform required">
                    <Select value={formData.appPlatform} onValueChange={(v) => handleChange('appPlatform', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {platformOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </FieldRow>

                <FieldRow>
                  <FieldGroup label="Do you have UI/UX designs ready?">
                    <Select value={formData.hasUiDesign} onValueChange={(v) => handleChange('hasUiDesign', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  {formData.hasUiDesign === 'no' && (
                    <FieldGroup label="Do you want us to design UI/UX?">
                      <Select value={formData.wantUiDesign} onValueChange={(v) => handleChange('wantUiDesign', v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  )}
                </FieldRow>

                <FieldGroup label="Core features required">
                  <Textarea
                    value={formData.appCoreFeatures}
                    onChange={(e) => handleChange('appCoreFeatures', e.target.value)}
                    placeholder="List the main features you need"
                    className="bg-white min-h-[60px]"
                  />
                </FieldGroup>

                <FieldRow>
                  <FieldGroup label="Do you need user authentication?">
                    <Select value={formData.needsAuth} onValueChange={(v) => handleChange('needsAuth', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  <FieldGroup label="Do you need payment integration?">
                    <Select value={formData.needsPayment} onValueChange={(v) => handleChange('needsPayment', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </FieldRow>

                {formData.needsAuth === 'yes' && (
                  <FieldGroup label="Login methods required">
                    <CheckboxGroup
                      options={loginMethods}
                      selectedValues={formData.authMethods}
                      onChange={(v) => handleMultiSelect('authMethods', v)}
                      cols={3}
                    />
                  </FieldGroup>
                )}

                {formData.needsPayment === 'yes' && (
                  <FieldGroup label="Payment type">
                    <Select value={formData.paymentType} onValueChange={(v) => handleChange('paymentType', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {paymentTypes.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                )}
              </div>
            </FormSection>
          )}

          {/* Digital Marketing Section */}
          {selectedServices.includes('digital-marketing') && (
            <FormSection title="Digital Marketing">
              <div className="space-y-4">
                <FieldRow>
                  <FieldGroup label="Primary goal">
                    <Select value={formData.marketingGoal} onValueChange={(v) => handleChange('marketingGoal', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {marketingGoals.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  <FieldGroup label="Target market / location">
                    <Input
                      value={formData.targetMarket}
                      onChange={(e) => handleChange('targetMarket', e.target.value)}
                      placeholder="e.g., USA, Europe, Global"
                      className="bg-white"
                    />
                  </FieldGroup>
                </FieldRow>

                <FieldGroup label="Services required">
                  <CheckboxGroup
                    options={marketingServices}
                    selectedValues={formData.marketingServicesNeeded}
                    onChange={(v) => handleMultiSelect('marketingServicesNeeded', v)}
                  />
                </FieldGroup>

                <FieldGroup label="Monthly marketing budget range">
                  <Select value={formData.marketingBudget} onValueChange={(v) => handleChange('marketingBudget', v)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {marketingBudgetRanges.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldGroup>

                {formData.marketingServicesNeeded.includes('seo') && (
                  <FieldRow>
                    <FieldGroup label="Website URL (for SEO)">
                      <Input
                        value={formData.seoWebsiteUrl}
                        onChange={(e) => handleChange('seoWebsiteUrl', e.target.value)}
                        placeholder="https://..."
                        className="bg-white"
                      />
                    </FieldGroup>
                    <FieldGroup label="Is SEO currently active?">
                      <Select value={formData.seoCurrentlyActive} onValueChange={(v) => handleChange('seoCurrentlyActive', v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  </FieldRow>
                )}

                {formData.marketingServicesNeeded.includes('ppc') && (
                  <FieldGroup label="Do you already have an ad account?">
                    <Select value={formData.hasAdAccount} onValueChange={(v) => handleChange('hasAdAccount', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                )}

                {formData.marketingServicesNeeded.includes('social') && (
                  <>
                    <FieldGroup label="Platforms required">
                      <CheckboxGroup
                        options={socialPlatforms}
                        selectedValues={formData.socialPlatforms}
                        onChange={(v) => handleMultiSelect('socialPlatforms', v)}
                        cols={4}
                      />
                    </FieldGroup>
                    <FieldGroup label="Do you need content creation?">
                      <Select value={formData.needsSocialContent} onValueChange={(v) => handleChange('needsSocialContent', v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  </>
                )}
              </div>
            </FormSection>
          )}

          {/* Logo & Branding Section */}
          {selectedServices.includes('logo') && (
            <FormSection title="Logo & Branding">
              <div className="space-y-4">
                <FieldRow>
                  <FieldGroup label="Is this a new brand or redesign?">
                    <Select value={formData.brandType} onValueChange={(v) => handleChange('brandType', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New brand</SelectItem>
                        <SelectItem value="redesign">Redesign</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  {formData.brandType === 'redesign' && (
                    <FieldGroup label="Upload existing logo">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="bg-white text-xs"
                      />
                    </FieldGroup>
                  )}
                </FieldRow>

                <FieldRow>
                  <FieldGroup label="Which type of logo are you looking for?">
                    <Select value={formData.logoType} onValueChange={(v) => handleChange('logoType', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {logoTypes.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  <FieldGroup label="Brand style preference">
                    <Select value={formData.brandStyle} onValueChange={(v) => handleChange('brandStyle', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {brandStyles.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </FieldRow>

                {formData.brandStyle === 'other' && (
                  <FieldGroup label="Describe brand style">
                    <Input
                      value={formData.brandStyleOther}
                      onChange={(e) => handleChange('brandStyleOther', e.target.value)}
                      placeholder="Describe your preferred style"
                      className="bg-white"
                    />
                  </FieldGroup>
                )}

                <FieldGroup label="Any logo references or competitors?">
                  <Textarea
                    value={formData.logoReferences}
                    onChange={(e) => handleChange('logoReferences', e.target.value)}
                    placeholder="Share links or describe logos you like"
                    className="bg-white min-h-[60px]"
                  />
                </FieldGroup>
              </div>
            </FormSection>
          )}

          {/* Animation / Video Section */}
          {selectedServices.includes('animation') && (
            <FormSection title="Animation / Explainer Video">
              <div className="space-y-4">
                <FieldRow>
                  <FieldGroup label="Type of video">
                    <Select value={formData.videoType} onValueChange={(v) => handleChange('videoType', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {videoTypes.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  <FieldGroup label="Desired duration">
                    <Select value={formData.videoDuration} onValueChange={(v) => handleChange('videoDuration', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {videoDurations.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </FieldRow>

                {formData.videoType === 'other' && (
                  <FieldGroup label="Describe video type">
                    <Input
                      value={formData.videoTypeOther}
                      onChange={(e) => handleChange('videoTypeOther', e.target.value)}
                      placeholder="Describe the video type"
                      className="bg-white"
                    />
                  </FieldGroup>
                )}

                <FieldRow>
                  <FieldGroup label="Do you have a script ready?">
                    <Select value={formData.hasScript} onValueChange={(v) => handleChange('hasScript', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  {formData.hasScript === 'no' && (
                    <FieldGroup label="Do you want us to write the script?">
                      <Select value={formData.wantScriptWriting} onValueChange={(v) => handleChange('wantScriptWriting', v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  )}
                </FieldRow>

                <FieldRow>
                  <FieldGroup label="Do you need voice-over?">
                    <Select value={formData.needsVoiceOver} onValueChange={(v) => handleChange('needsVoiceOver', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </FieldRow>

                {formData.needsVoiceOver === 'yes' && (
                  <FieldRow>
                    <FieldGroup label="Accent preference">
                      <Select value={formData.accentPreference} onValueChange={(v) => handleChange('accentPreference', v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {accentOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                    <FieldGroup label="Voice preference">
                      <Select value={formData.voicePreference} onValueChange={(v) => handleChange('voicePreference', v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {voiceOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  </FieldRow>
                )}
              </div>
            </FormSection>
          )}

          {/* eLearning Section */}
          {selectedServices.includes('elearning') && (
            <FormSection title="eLearning Development">
              <div className="space-y-4">
                <FieldRow>
                  <FieldGroup label="Type of course">
                    <Select value={formData.courseType} onValueChange={(v) => handleChange('courseType', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {elearningTypes.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  <FieldGroup label="Approx. number of modules/slides">
                    <Input
                      value={formData.moduleCount}
                      onChange={(e) => handleChange('moduleCount', e.target.value)}
                      placeholder="e.g., 10 modules, 50 slides"
                      className="bg-white"
                    />
                  </FieldGroup>
                </FieldRow>

                <FieldRow>
                  <FieldGroup label="Is course content ready?">
                    <Select value={formData.courseContentReady} onValueChange={(v) => handleChange('courseContentReady', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="partially">Partially</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                  {(formData.courseContentReady === 'partially' || formData.courseContentReady === 'no') && (
                    <FieldGroup label="Do you want us to structure/create content?">
                      <Select value={formData.wantContentStructure} onValueChange={(v) => handleChange('wantContentStructure', v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  )}
                </FieldRow>

                <FieldGroup label="Do you need an LMS?">
                  <Select value={formData.needsLms} onValueChange={(v) => handleChange('needsLms', v)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="already-have">Already have one</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldGroup>

                {formData.needsLms === 'already-have' && (
                  <FieldGroup label="Which LMS are you using?">
                    <Input
                      value={formData.existingLms}
                      onChange={(e) => handleChange('existingLms', e.target.value)}
                      placeholder="e.g., Moodle, Teachable"
                      className="bg-white"
                    />
                  </FieldGroup>
                )}

                {formData.needsLms === 'yes' && (
                  <FieldGroup label="Preferred LMS (if any)">
                    <Input
                      value={formData.preferredLms}
                      onChange={(e) => handleChange('preferredLms', e.target.value)}
                      placeholder="e.g., Moodle, Teachable, or no preference"
                      className="bg-white"
                    />
                  </FieldGroup>
                )}
              </div>
            </FormSection>
          )}

          {/* LinkedIn Section */}
          {selectedServices.includes('linkedin') && (
            <FormSection title="LinkedIn Services">
              <div className="space-y-4">
                <FieldGroup label="Profile type">
                  <Select value={formData.linkedinProfileType} onValueChange={(v) => handleChange('linkedinProfileType', v)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {linkedinProfileTypes.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldGroup>

                <FieldGroup label="What do you need help with?">
                  <CheckboxGroup
                    options={linkedinServices}
                    selectedValues={formData.linkedinServicesNeeded}
                    onChange={(v) => handleMultiSelect('linkedinServicesNeeded', v)}
                  />
                </FieldGroup>

                {formData.linkedinServicesNeeded.includes('content') && (
                  <FieldGroup label="Posting frequency">
                    <Select value={formData.postingFrequency} onValueChange={(v) => handleChange('postingFrequency', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {postingFrequency.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                )}

                {formData.linkedinServicesNeeded.includes('leads') && (
                  <>
                    <FieldGroup label="Target audience (role, industry, country)">
                      <Input
                        value={formData.targetAudience}
                        onChange={(e) => handleChange('targetAudience', e.target.value)}
                        placeholder="e.g., CEOs in SaaS, USA"
                        className="bg-white"
                      />
                    </FieldGroup>
                    <FieldRow>
                      <FieldGroup label="Do you already have outreach messages?">
                        <Select value={formData.hasOutreachMessages} onValueChange={(v) => handleChange('hasOutreachMessages', v)}>
                          <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </FieldGroup>
                      {formData.hasOutreachMessages === 'no' && (
                        <FieldGroup label="Do you want us to create them?">
                          <Select value={formData.wantOutreachCreation} onValueChange={(v) => handleChange('wantOutreachCreation', v)}>
                            <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </FieldGroup>
                      )}
                    </FieldRow>
                  </>
                )}
              </div>
            </FormSection>
          )}

          {/* Content Writing Section */}
          {selectedServices.includes('content-writing') && (
            <FormSection title="Content Writing">
              <div className="space-y-4">
                <FieldGroup label="What type of content do you need?">
                  <CheckboxGroup
                    options={contentTypes}
                    selectedValues={formData.contentTypesNeeded}
                    onChange={(v) => handleMultiSelect('contentTypesNeeded', v)}
                    cols={1}
                  />
                </FieldGroup>

                <FieldGroup label="Additional details about your content needs">
                  <Textarea
                    value={formData.contentDetails}
                    onChange={(e) => handleChange('contentDetails', e.target.value)}
                    placeholder="Describe topics, tone, target audience, etc."
                    className="bg-white min-h-[80px]"
                  />
                </FieldGroup>
              </div>
            </FormSection>
          )}

          {/* Custom Package Note */}
          {selectedPackage === 'custom' && selectedServices.length === 0 && (
            <FormSection title="Custom Project" color="amber">
              <p className="text-sm text-amber-600 mb-4">
                Tell us about your idea in the notes below. We'll schedule a free consultation to understand your needs better.
              </p>
            </FormSection>
          )}

          {/* Project Clarity Section */}
          <FormSection title="Project Clarity & Alignment">
            <div className="space-y-4">
              <FieldRow>
                <FieldGroup label="Who will be the primary decision-maker?">
                  <Select value={formData.decisionMaker} onValueChange={(v) => handleChange('decisionMaker', v)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="me">Me</SelectItem>
                      <SelectItem value="someone-else">Someone else</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldGroup>
                {formData.decisionMaker === 'someone-else' && (
                  <FieldGroup label="Decision-maker's role and availability">
                    <Input
                      value={formData.decisionMakerDetails}
                      onChange={(e) => handleChange('decisionMakerDetails', e.target.value)}
                      placeholder="e.g., CEO, available weekly"
                      className="bg-white"
                    />
                  </FieldGroup>
                )}
              </FieldRow>

              <FieldGroup label="What does success look like? Any non-negotiables?">
                <Textarea
                  value={formData.successAndNonNegotiables}
                  onChange={(e) => handleChange('successAndNonNegotiables', e.target.value)}
                  placeholder="Describe your goals and any must-haves for this project"
                  className="bg-white min-h-[80px]"
                />
              </FieldGroup>

              <FieldGroup label="After launch, will you need help with? (optional)">
                <CheckboxGroup
                  options={postLaunchServices}
                  selectedValues={formData.postLaunchNeeds}
                  onChange={(v) => handleMultiSelect('postLaunchNeeds', v)}
                />
              </FieldGroup>

              <FieldGroup label="Anything else you'd like us to know?">
                <Textarea
                  value={formData.additionalNotes}
                  onChange={(e) => handleChange('additionalNotes', e.target.value)}
                  placeholder="Share any additional details, requirements, or questions..."
                  className="bg-white min-h-[80px]"
                />
              </FieldGroup>
            </div>
          </FormSection>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || !formData.fullName}
            className={cn(
              'w-full h-12 text-base font-medium transition-all duration-300',
              'bg-emerald-600 hover:bg-emerald-700 text-white',
              'disabled:bg-slate-200 disabled:text-slate-400'
            )}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit Project Request
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};
